import {
  concernTagLabel,
  isExamplePost,
  MENU_TAG_AXES,
  menuTagAxisLabel,
  menuTagLabel,
  normalizeConcernTags,
  normalizeMenuTags,
  type Axis,
  type MenuTagAxis,
  type MenuTags,
  type PageQuery,
  type PostProduct,
  type PostSource,
} from '@repo/shared'
import type { AdminPost, Page } from '../api'

/**
 * 커뮤니티 새 칸(0026: 예시 글 표시 source, 고민 유형 concernTags, 메뉴 꼬리표 menuTags)과
 * 제품 미니 카드의 원가·마진 공개 여부를 어드민에서 보여 주고 거른다 (2026-10-23).
 * 서버 목록은 페이지만 받으므로 거르기는 여기서 한다. 기준은 앱 칩과 같다 (api lib/community-concerns.ts)
 */

/** 고민 유형 id 를 한국어 라벨로. 모르는 값은 버린다 */
export function concernLabels(
  tags: readonly string[] | null | undefined,
): { id: string; label: string }[] {
  return normalizeConcernTags(tags).map((id) => ({ id, label: concernTagLabel(id, 'ko') }))
}

const tagIdsOf = (tags: MenuTags | null | undefined, axis: MenuTagAxis): unknown[] => {
  const ids: unknown = tags?.[axis]
  return Array.isArray(ids) ? ids : []
}

/**
 * 고민 유형 하나에 걸리는 글. 글이 고른 유형에 더해 메뉴 묶음 4개는 붙인 제품 분류와 메뉴 꼬리표로도 걸린다
 * (api hasConcern 의 menuConcernAlso 와 같은 규칙, 반박 검토 M8)
 */
export function matchesConcern(
  p: Pick<AdminPost, 'concernTags' | 'menuTags' | 'product'>,
  id: string,
): boolean {
  if (normalizeConcernTags(p.concernTags).some((t) => t === id)) return true
  switch (id) {
    case 'intl-main':
      return p.product?.axis === 'menu' && p.product.majorId === 'intl'
    case 'technique':
      return tagIdsOf(p.menuTags, 'technique').length > 0
    case 'dietary':
      return tagIdsOf(p.menuTags, 'diet').length > 0
    case 'testing':
      return tagIdsOf(p.menuTags, 'status').includes('testing')
    default:
      return false
  }
}

/** all = 전체, example = 운영팀 예시 글만, user = 사용자 글만 */
export type PostSourceFilter = 'all' | PostSource
export interface PostFilter {
  source: PostSourceFilter
  /** 고민 유형 id. null 이면 거르지 않는다 */
  concern: string | null
}
export const NO_POST_FILTER: PostFilter = { source: 'all', concern: null }

export function hasPostFilter(f: PostFilter): boolean {
  return f.source !== 'all' || f.concern !== null
}

export function filterPosts<T extends AdminPost>(items: readonly T[], f: PostFilter): T[] {
  return items.filter((p) => {
    if (f.source !== 'all' && isExamplePost(p) !== (f.source === 'example')) return false
    return f.concern === null || matchesConcern(p, f.concern)
  })
}

/**
 * 제품 미니 카드의 원가·마진. 서버는 올린 사람이 공개(shareCost)를 켰을 때만 unitCost·marginPct 를 채운다.
 * none = 붙인 제품 없음, shared = 앱 카드에 원가·마진이 보인다, hidden = 판매가만 (기본 끔, 옛 글 포함)
 */
type CostShare = 'none' | 'shared' | 'hidden'
export function costShareOf(product: PostProduct | null | undefined): CostShare {
  if (!product) return 'none'
  return product.unitCost != null || product.marginPct != null ? 'shared' : 'hidden'
}

export interface MenuTagRow {
  axis: MenuTagAxis
  axisLabel: string
  labels: string[]
}

/** 메뉴 꼬리표를 축 줄로. 앱과 같은 normalizeMenuTags 를 거친다 (식품 축은 꼬리표가 없다) */
export function menuTagRows(tags: MenuTags | null | undefined, axis: Axis): MenuTagRow[] {
  const clean = normalizeMenuTags(tags, axis)
  return MENU_TAG_AXES.flatMap((a) => {
    const ids = clean[a]
    if (!ids?.length) return []
    return [
      {
        axis: a,
        axisLabel: menuTagAxisLabel(a, 'ko'),
        labels: ids.map((id) => menuTagLabel(a, id, 'ko')),
      },
    ]
  })
}

/** 거를 때 받는 한 쪽 크기 (pageQuerySchema 상한) */
const SCAN_PER_PAGE = 100
/** 거를 때 받는 최대 쪽수. 넘으면 최근 글 2,000건 안에서만 거른다 */
const SCAN_MAX_PAGES = 20

export interface AllPages<T> {
  items: T[]
  /** 서버가 알려 준 전체 글 수 */
  total: number
  /** 최대 쪽수에서 멈춰 오래된 글을 받지 못했다 */
  truncated: boolean
}

/**
 * 거르기를 켜면 목록을 최근 글부터 끝까지 받는다. 받는 사이 새 글이 올라와 쪽이 밀리면 같은 글이
 * 두 번 올 수 있어 id 로 한 번만 둔다
 */
export async function loadAllPages<T extends { id: string }>(
  fetchPage: (q: PageQuery) => Promise<Page<T>>,
  maxPages = SCAN_MAX_PAGES,
): Promise<AllPages<T>> {
  const seen = new Set<string>()
  const items: T[] = []
  let total = 0
  for (let page = 1; page <= maxPages; page++) {
    const res = await fetchPage({ page, perPage: SCAN_PER_PAGE })
    total = res.total
    for (const item of res.items) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      items.push(item)
    }
    if (res.items.length < SCAN_PER_PAGE || page * SCAN_PER_PAGE >= total) {
      return { items, total, truncated: false }
    }
  }
  return { items, total, truncated: true }
}
